import { CouponActivityEventType } from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import type { CouponActivityRecord } from 'noddi-async';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { ApiErrorMessageInternal, NoddiCard, NoddiSpinner, usePaginationQueryParam } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { usePromotionBrandScope } from '../../usePromotionBrandScope';

type Props = {
  couponId: number;
};

const EVENT_META: Record<CouponActivityEventType, { icon: NoddiIconName; phrase: string }> = {
  [CouponActivityEventType.ISSUED]: { icon: 'Plus', phrase: 'issued the coupon' },
  [CouponActivityEventType.UPDATED]: { icon: 'Edit', phrase: 'updated the coupon' },
  [CouponActivityEventType.USED]: { icon: 'Check', phrase: 'marked the coupon as used' },
  [CouponActivityEventType.DELETED]: { icon: 'Trash2', phrase: 'deleted the coupon' }
};

export const CouponActivityTab = ({ couponId }: Props) => {
  const { serviceOrganizationId, useStaffCouponWrite } = usePromotionBrandScope();
  const { pagination } = usePaginationQueryParam();

  // Admin reads the staff list: the org-scoped endpoint 404s for brand coupons
  // outside the one service organization the admin scope picked.
  const staffActivities = noddiAsync.useGet({
    type: URLKeys.getPaginatedCouponActivities,
    input: { couponIds: [couponId], ...pagination },
    queryConfig: {
      enabled: !!useStaffCouponWrite,
      placeholderData: keepPreviousData
    }
  });
  const organizationActivities = noddiAsync.useGet({
    type: URLKeys.getPaginatedServiceOrganizationCouponActivities,
    input: { serviceOrganizationId: serviceOrganizationId ?? 0, couponId, ...pagination },
    queryConfig: {
      enabled: !useStaffCouponWrite && serviceOrganizationId != null,
      placeholderData: keepPreviousData
    }
  });
  const { data, isPending, error } = useStaffCouponWrite ? staffActivities : organizationActivities;

  if (!useStaffCouponWrite && serviceOrganizationId == null) {
    return <p className='text-sm text-muted-foreground'>Activity is unavailable for this brand.</p>;
  }

  if (error) {
    return <ApiErrorMessageInternal error={error} />;
  }

  if (isPending) {
    return (
      <div className='flex items-center justify-center py-10'>
        <NoddiSpinner />
      </div>
    );
  }

  const activities = data?.results ?? [];

  return (
    <NoddiCard title='Activity' size='small' icon='Activity' iconTile>
      {activities.length === 0 ? (
        <p className='text-sm text-muted-foreground'>No activity yet.</p>
      ) : (
        <div className='flex flex-col gap-4'>
          {activities.map((activity) => (
            <ActivityRow key={activity.id} activity={activity} />
          ))}
        </div>
      )}
    </NoddiCard>
  );
};

const ActivityRow = ({ activity }: { activity: CouponActivityRecord }) => {
  const actorName = activity.actorName ?? 'System';
  const timestamp = noddiFormatDate(activity.createdAt, NoddiDateFormats.DATE_TIME);
  const eventType = activity.eventType.value;
  const meta = EVENT_META[eventType];
  const icon = meta?.icon ?? 'Circle';
  const phrase = meta?.phrase ?? eventType.toLowerCase().replaceAll('_', ' ');
  const detail = activity.detail?.trim() || null;

  return (
    <div className='flex items-start gap-3'>
      <div className={cn('mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-muted')}>
        <NoddiIcon name={icon} size='small' className='text-muted-foreground' />
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <div className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm'>
          <span className='font-medium'>{actorName}</span>
          <span className='text-muted-foreground'>{phrase}</span>
          <span className='ml-auto text-xs text-muted-foreground'>{timestamp}</span>
        </div>
        {detail ? <p className='text-xs whitespace-pre-wrap text-muted-foreground'>{detail}</p> : null}
      </div>
    </div>
  );
};
